import { MouseEvent, useCallback, useEffect, useRef, useState } from 'react';
import {
  IEventHandlerOptions,
  eventHandler,
} from '@shared/helpers/event.helpers';
import { useIsomorphicLayoutEffect } from 'usehooks-ts';
import { is } from '@client/helpers/dom.guards';
import { guards } from '@shared/helpers/typeguards.helpers';

/**
 * Create a mouse event handler that can be used in JSX.
 * @param fn
 */
export function useMouseEventCallback<R>(
  fn: ((e: MouseEvent) => R) | undefined,
  options?: IEventHandlerOptions,
): (e: MouseEvent) => R | undefined {
  return useEventCallback(fn, options);
}

/**
 * Create a keyboard event handler that can be used in JSX.
 * @param fn
 */
export function useKeyboardEventCallback<
  T extends KeyboardEvent | React.KeyboardEvent,
  R = any,
  F = T extends React.KeyboardEvent
    ? React.KeyboardEventHandler
    : (e: T) => R | undefined,
>(fn: ((e: T) => R) | undefined, options?: IEventHandlerOptions): F {
  return useEventCallback(fn, options) as F;
}

/**
 * Create an event handler that can be used in JSX.
 * Based on useEventCallback from 'usehooks-ts'
 * @param fn
 */
function useEventCallback<
  E extends MouseEvent | KeyboardEvent | React.KeyboardEvent,
  R,
>(
  fn: ((e: E) => R) | undefined,
  options?: IEventHandlerOptions,
): (e: E) => R | undefined {
  const ref = useRef<typeof fn>(() => {
    throw new Error('Cannot call an event handler while rendering.');
  });
  const {
    stopPropagation,
    preventDefault,
    ignoreInputEvents,
    ignoreOverlayEvents,
    ignoreOnlyTextInputForKeyboard,
  } = options || {};

  useIsomorphicLayoutEffect(() => {
    ref.current = fn;
  }, [fn]);

  return useCallback(
    (e) => {
      return eventHandler<E, R>(ref.current, {
        ignoreInputEvents,
        preventDefault,
        stopPropagation,
        ignoreOverlayEvents,
        ignoreOnlyTextInputForKeyboard,
      })(e);
    },

    [
      ignoreInputEvents,
      preventDefault,
      stopPropagation,
      ignoreOverlayEvents,
      ignoreOnlyTextInputForKeyboard,
    ],
  ) as (e: E) => R;
}

export function useKeydownListener(
  fn: (event: KeyboardEvent) => void,
  key: KeyboardEvent['key'],
): void {
  useEffect(() => {
    const listener = (event: KeyboardEvent) => {
      if (event.key === key) {
        fn(event);
      }
    };
    global.document.addEventListener('keydown', listener);
    return () => {
      global.document.removeEventListener('keydown', listener);
    };
  }, [fn, key]);
}

/**
 * Add and remove an event listener for a specific event type.
 * @param type
 * @param listener
 */
export function useWindowEventListener(
  type: keyof WindowEventMap,
  listener: (event: WindowEventMap[typeof type]) => void,
  // true means capture phase, false means bubble phase, AddEventListenerOptions is the options object
  options?: boolean | AddEventListenerOptions,
): void {
  useEffect(() => {
    window.addEventListener(type, listener, options);
    return () => window.removeEventListener(type, listener, options);
  }, [type, listener, options]);
}

export const useFocusInside = (ref?: React.RefObject<HTMLElement | null>) => {
  const selfRef = useRef<HTMLElement | null>(null);
  const activeRef = ref ?? selfRef;
  const [isFocused, setIsFocused] = useState(false);
  useEffect(() => {
    // initialize from current active element
    if (guards.is.defined(activeRef.current)) {
      const active = document.activeElement;
      setIsFocused(
        is.Element(active) ? activeRef.current.contains(active) : false,
      );
    }

    const handleFocusIn = (e: FocusEvent) => {
      if (!guards.is.defined(activeRef.current)) return;
      if (!is.Element(e.target)) return setIsFocused(false);
      setIsFocused(activeRef.current.contains(e.target));
    };

    const handleFocusOut = (e: FocusEvent) => {
      if (!guards.is.defined(activeRef.current)) return;
      // target had focus, relatedTarget is the one who will get focus next
      const { target: current, relatedTarget: next } = e;
      const wasInside =
        is.Element(current) && activeRef.current.contains(current);
      const nextInside = is.Element(next) && activeRef.current.contains(next);
      if (!wasInside || nextInside) return;
      // relatedTarget is often null while focus never left (removed node, window flicker).
      const active = document.activeElement;
      if (is.Element(active) && activeRef.current.contains(active)) return;
      setIsFocused(false);
    };
    document.body.addEventListener('focusout', handleFocusOut);
    document.body.addEventListener('focusin', handleFocusIn);
    return () => {
      document.body.removeEventListener('focusin', handleFocusIn);
      document.body.removeEventListener('focusout', handleFocusOut);
    };
  }, [activeRef]);
  return [isFocused, activeRef] as const;
};
